import React, { useEffect, useState } from 'react';
import Select from 'react-select';

import { DatePickerInput } from '../DatePickerInput';
import { AssetsService, DeviceDataClassificationsService, DevicesService } from '../../services';
import i18next from "i18next";

import config from "config";

const AssetComparisonReportForm = ({ availableGroupings, onSubmit }) => {
    const [dateRange, setDateRange] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [availableDeviceValues, setAvailableDeviceValues] = useState(new Map());
    const [assetsToCompare, setAssetsToCompare] = useState([{selectedGrouping: null, selectedMeasure: null}]);
    const [workingRow, setWorkingRow] = useState(0);
    const [selectedGrouping, setSelectedGrouping] = useState(null);
    const [selectedMeasure, setSelectedMeasure] = useState(null);
    const [availableMeasurementClassifications, setAvailableMeasurementClassifications] = useState(null);
    const [selectedMeasumentClassification, setSelectedMeasurementClassification] = useState(null);

    const timeframeOptions = [
        { value: 0, label: i18next.t("form-labels.day") },
        { value: 1, label: i18next.t("form-labels.week") },
        { value: 2, label: i18next.t("form-labels.month") },
    ];
    const maxAssetsToCompare = 5;

    useEffect(() => {
        DeviceDataClassificationsService.listAll().then((response) => {
            setAvailableMeasurementClassifications(
                response.deviceDataClassifications.sort((ddcA, ddcB) => ddcA.name.localeCompare(ddcB.name)).map((ddc) => ({ value: ddc.id, label: ddc.name, classification: ddc }))
            );
        }).catch((err) => {
            console.error(err);
            // TODO handle error
        });
    }, []);

    useEffect(() => {
        if (selectedGrouping) {
            let localAssetToCompare = [...assetsToCompare];
            localAssetToCompare[workingRow].selectedGrouping = selectedGrouping;
            localAssetToCompare[workingRow].selectedMeasure = null;
            localAssetToCompare[workingRow].loading = true;

            let localAvailableDeviceValues = new Map(availableDeviceValues);

            setAssetsToCompare(localAssetToCompare);

            let requests = [];

            requests.push(AssetsService.listAllForGrouping(selectedGrouping.grouping.id));
            requests.push(DevicesService.listAllForGrouping(selectedGrouping.grouping.id, false, false, true));
            
            Promise.all(requests).then((responses) => {
                const deviceMeasurements = responses.flatMap((response) => {
                    if (response.devices) {

                        return response.devices.flatMap((device) => {
                            return device.deviceType.dataTypes.map((dt) => {
                                if (dt.deviceDataClassification?.id == selectedMeasumentClassification.classification.id) {
                                    return {
                                        value: `${device.serialNumber}:${dt.token}`,
                                        label: `${device.displayName} : ${dt.name}`,
                                        device: device,
                                        dataType: dt,
                                    };
                                } else {
                                    return null;
                                }
                            });
                        }).filter((value) => value != null);

                    } else if (response.assets) {

                        return response.assets.flatMap((asset) => {
                            return asset.assetMeasures.map((assetMeasure) => {
                                if (assetMeasure.endAt == null && assetMeasure.deviceDataType.deviceDataClassification.id == selectedMeasumentClassification.classification.id) {
                                    return {
                                        value: `${asset.serialNumber}:${assetMeasure.deviceDataType.token}`,
                                        label: `${asset.name} : ${assetMeasure.assetTypeDeviceDataType.name}`,
                                        device: assetMeasure.device,
                                        dataType: assetMeasure.deviceDataType,
                                    };
                                } else {
                                    return null;
                                }
                            });
                        }).filter((value) => value != null);

                    }
                });

                localAvailableDeviceValues.set(selectedGrouping.grouping.id, deviceMeasurements);                

                localAssetToCompare[workingRow].loading = false;

                setAssetsToCompare(localAssetToCompare);
                setAvailableDeviceValues(localAvailableDeviceValues);
            }).catch(err => {
                console.error(err);

                localAvailableDeviceValues.set(selectedGrouping.grouping.id, []);
                localAssetToCompare[workingRow].loading = false;
                
                setAssetsToCompare(localAssetToCompare);
                setAvailableDeviceValues(localAvailableDeviceValues);
            });
        }
    }, [selectedGrouping]);

    useEffect(() => {
        if (selectedMeasure) {
            let localDeviceToCompare = [...assetsToCompare];

            localDeviceToCompare[workingRow].selectedMeasure = selectedMeasure;

            if (localDeviceToCompare.length === 1) {
                const selectedGroupingId = localDeviceToCompare[0].selectedGrouping.value;
                const filteredAvailableDevices = availableDeviceValues.get(selectedGroupingId); 
                
                let localAvailableDeviceValues = new Map();
                localAvailableDeviceValues.set(selectedGroupingId, filteredAvailableDevices);
                setAvailableDeviceValues(localAvailableDeviceValues);

                // Automatically add a new entry since there is only one
                localDeviceToCompare.push({ selectedGrouping: localDeviceToCompare[0].selectedGrouping, selectedMeasure: null });
                setAssetsToCompare(localDeviceToCompare);
            } else {
                setAssetsToCompare(localDeviceToCompare);
            }
        }
    }, [selectedMeasure]);

    const handleRowDelete = (index) => {
        let localAssetToCompare = [...assetsToCompare];
        localAssetToCompare.splice(index, 1);

        if (localAssetToCompare.length === 1) {
            let { selectedGrouping } = localAssetToCompare[0];

            setAssetsToCompare(localAssetToCompare);
            setWorkingRow(0);
            setSelectedGrouping(selectedGrouping);
        } else {
            setAssetsToCompare(localAssetToCompare);
        }
    };

    const handleRowAdd = () => {
        if (assetsToCompare.length > 0 && assetsToCompare.length <= maxAssetsToCompare) {
            let localAssetToCompare = [...assetsToCompare];
            const prevSelectedGrouping = localAssetToCompare[localAssetToCompare.length - 1].selectedGrouping;

            localAssetToCompare.push({ selectedGrouping: prevSelectedGrouping, selectedMeasure: null });
            setAssetsToCompare(localAssetToCompare);
        }
    };

    const handleSubmit = () => {
        event.preventDefault();

        let displayNameToMeasurementId = {};
        assetsToCompare.forEach((selection) => {
            displayNameToMeasurementId[`${selection.selectedMeasure.device.serialNumber}:${selection.selectedMeasure.dataType.token}`] = `${selection.selectedGrouping.label} ${selection.selectedMeasure.label}`;
        });

        onSubmit({
            dateRange: dateRange,
            startDate: startDate,
            measurementIds: assetsToCompare.map((selection) => `${selection.selectedMeasure.device.serialNumber}:${selection.selectedMeasure.dataType.token}`),
            deviceDataType: selectedMeasumentClassification.classification,
            displayNameToMeasurementId: displayNameToMeasurementId
        });
    };

    return availableMeasurementClassifications ? (
        <div className="op-content-box filter-box" style={{marginBottom: 20}}>
            <ul className="list-inline clearfix">
                <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('filters') }</h4></li>
            </ul>
            <div className = "row">
                <div className="col-sm-3">
                    <label>{ i18next.t('timeframe') }</label>
                    <Select
                        options={timeframeOptions}
                        clearable={false}
                        value={dateRange}
                        onChange={(selectedDateRange) => setDateRange(selectedDateRange)}
                        isSearchable={false}
                        classNamePrefix="react-select"
                        placeholder={ i18next.t('form-labels.select-timeframe') }
                    />
                </div>
                <div className="pull-left" style={{"maxWidth": "150px", margin: "0px 0px 0px 10px"}}>
                    <label>{ i18next.t('form-labels.start-date') }</label>
                    <DatePickerInput input={{value: startDate}} meta={{}} onDayChange={(selectedDate) => setStartDate(selectedDate)} />
                </div>
                <div className="col-sm-3 pull-left" style={{"maxWidth": "300px", margin: "0px 0px 0px 10px"}}>
                    <label>Measurement Type</label>
                    <Select
                        options={availableMeasurementClassifications}
                        clearable={false}
                        value={selectedMeasumentClassification}
                        onChange={(ddc) => setSelectedMeasurementClassification(ddc)}
                        classNamePrefix="react-select"
                        isDisabled={assetsToCompare != null && assetsToCompare.length > 0 && assetsToCompare[0]?.selectedGrouping != null}
                    />
                </div>
            </div>
            { 
                dateRange && startDate && selectedMeasumentClassification && assetsToCompare.map((entity, index) => 
                    <div key={index} className = "row" style={{marginTop: 10}}>
                        <div className="col-sm-3">
                            <label>{ i18next.t('form-labels.location') }</label>
                            <Select
                                options={availableGroupings}
                                clearable={false}
                                value={entity.selectedGrouping}
                                onChange={(selection) => { setWorkingRow(index); setSelectedGrouping(selection); }}
                                classNamePrefix="react-select"
                                placeholder ={ i18next.t('form-labels.select-location') }
                            />
                        </div>
                        { 
                            entity.selectedGrouping && 
                            <div className="col-sm-5">
                                <label>Measurement</label>
                                <Select
                                    options={availableDeviceValues.get(entity.selectedGrouping.value)}
                                    clearable={false}
                                    value={entity.selectedMeasure}
                                    onChange={(selection) => { setWorkingRow(index); setSelectedMeasure(selection); }}
                                    classNamePrefix="react-select"
                                    placeholder ={ i18next.t('form-labels.select-asset') }
                                    isLoading={entity.loading}
                                />
                            </div>
                        }
                        { 
                            entity.selectedGrouping && entity.selectedMeasure &&
                            <div className="col-sm-4" style={{'marginTop': '20px'}}>
                                <img style={{paddingRight: 15}} src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`} alt={ i18next.t('form-labels.delete') } onClick={() => handleRowDelete(index)}/>
                                { 
                                    index > 0 && index === assetsToCompare.length - 1 &&
                                    <span>
                                    { 
                                        assetsToCompare.length < maxAssetsToCompare && 
                                            <img style={{paddingRight: 15}} src={`${config.WEB_BASE_URL}/images/icon/add-button.svg`} alt={ i18next.t('form-labels.add') } onClick={handleRowAdd}/>
                                    }
                                    { 
                                        assetsToCompare.length > 1 &&
                                            <span><button type="submit" onClick = {handleSubmit} className="btn btn-sm btn-default">{ i18next.t('form-labels.run-report') }</button></span>
                                    }
                                    </span>
                                }
                            </div>
                        }
                    </div>
                )
            }
        </div>
    ) : (<div className="loader">{ i18next.t('loading') }</div>);
}

export default AssetComparisonReportForm;